import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, UpsellingButton as F1UpsellButton } from '@factorialco/factorial-one-react'
import { Check } from '@factorialco/factorial-one-react/icons/app'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useCreateUpsellRequestWithErrorHandling } from 'modules/upselling_kit/hooks/useCreateUpsellRequest'
import { useUpsell } from 'modules/upselling_kit/lib/upsellContext'
import { UpsellingKitModule } from 'modules/upselling_kit/types'

import { ProductModal } from '../ProductModal'
import { UpsellGuard } from '../UpsellGuard'

interface BaseUpsellButtonProps {
  requestSource: string
  pageSource: string
  label?: string
  size?: 'sm' | 'md' | 'lg'
  showModuleModal?: boolean
  onClick?: () => void
}

type UpsellButtonProps = UpsellingKitModule & BaseUpsellButtonProps

const UpsellButtonContent = ({
  label,
  size,
  requestSource,
  requestReason,
  featureRequested,
  moduleRequested,
  pageSource,
  showModuleModal = false,
}: UpsellButtonProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false)
  const { create } = useCreateUpsellRequestWithErrorHandling()

  const handleRequest = async () => {
    track(trackingEvents.upsellingKit.clickProductUpsellComponent, {
      source: requestSource,
      component: 'upselling_button',
      module: moduleRequested,
    })
    if (showModuleModal) {
      setIsModuleModalOpen(true)
    } else {
      await create({
        moduleRequested,
        requestSource,
        requestReason,
        featureRequested,
        pageSource,
      })
    }
  }

  const handleClose = () => {
    track(trackingEvents.upsellingKit.dismissedProductUpsellComponent, {
      source: requestSource,
      component: 'upselling_button',
      module: moduleRequested,
    })
  }

  useEffect(() => {
    track(trackingEvents.upsellingKit.viewedProductUpsellComponent, {
      source: requestSource,
      component: 'upselling_button',
      module: moduleRequested,
    })
  }, [requestSource, moduleRequested])

  const getModalProps = () => {
    if (
      requestReason === ValidReasons.MissingFeature ||
      requestReason === ValidReasons.MissingFeatureLegacy
    ) {
      return {
        moduleRequested,
        requestSource,
        requestReason,
        featureRequested,
        pageSource,
        onClose: () => setIsModuleModalOpen(false),
      } as const
    }

    return {
      moduleRequested,
      requestSource,
      requestReason,
      pageSource,
      onClose: () => setIsModuleModalOpen(false),
    } as const
  }

  return (
    <>
      <F1UpsellButton
        label={label ?? i18n.t('upselling_kit.button.request_information')}
        size={size}
        showConfirmation={!showModuleModal}
        onRequest={handleRequest}
        errorMessage={{
          title: i18n.t('upselling_kit.upselling_button.error_message.title'),
          description: i18n.t('upselling_kit.upselling_button.error_message.description'),
        }}
        successMessage={{
          title: i18n.t('upselling_kit.upselling_button.success_message.title'),
          description: i18n.t('upselling_kit.upselling_button.success_message.description'),
          buttonLabel: i18n.t('upselling_kit.upselling_button.success_message.button_label'),
          buttonOnClick: () => {
            navigate(navigation.discover.marketplaceModules.fullPath())
          },
        }}
        loadingState={{
          label: i18n.t('upselling_kit.upselling_button.loading_state.label'),
        }}
        nextSteps={{
          title: i18n.t('upselling_kit.upselling_button.next_steps.title'),
          items: [
            {
              text: i18n.t('upselling_kit.upselling_button.next_steps.item_1'),
              isCompleted: true,
            },
            {
              text: i18n.t('upselling_kit.upselling_button.next_steps.item_2'),
              isCompleted: false,
            },
            {
              text: i18n.t('upselling_kit.upselling_button.next_steps.item_3'),
              isCompleted: false,
            },
          ],
        }}
        onClose={handleClose}
        closeLabel={i18n.t('upselling_kit.upselling_button.close_label')}
      />
      {isModuleModalOpen && (
        <ProductModal
          {...getModalProps()}
          isOpen={isModuleModalOpen}
          onClose={() => setIsModuleModalOpen(false)}
        />
      )}
    </>
  )
}

const FallbackButton = () => {
  const { isAlreadyInstalled } = useUpsell()

  if (isAlreadyInstalled) {
    return (
      <Button
        variant="outline"
        label={i18n.t('product_updates.in_your_plan')}
        icon={Check}
        disabled
      />
    )
  } else {
    return null
  }
}

const UpsellButton = ({ label, size, fallbackComponent, ...upsellProps }: UpsellButtonProps) => {
  return (
    <UpsellGuard {...upsellProps} fallbackComponent={fallbackComponent || <FallbackButton />}>
      <UpsellButtonContent {...upsellProps} label={label} size={size} />
    </UpsellGuard>
  )
}

export { UpsellButton }
